import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosClient, getBaseUrlVersioned } from "../../../services/axiosClient";
import { API_VERSION } from "../../types";
import { UserVoiceActions } from "./types";
const API_PATH_VOICES = "users/voices";
/**
 * NOTE: This slice is currently using mock data.
 * Replace mock implementations with real API calls once BE endpoints are available.
 *
 * IMPORTANT:
 * We're keeping this slice separate from voiceSlice for now.
 * Pending final decision after BE endpoints are defined and team feedback is collected.
 * See discussion: https://nexdayai.slack.com/archives/C09LS0Q43DH/p1764671414065819?threa
  d_ts=1764665206.737599&cid=C09LS0Q43DH
 */
export const buildVoicesMap = (voices) => {
    return voices.reduce((acc, voice) => {
        acc[voice.id] = voice;
        return acc;
    }, {});
};
const initialState = {
    userVoices: [],
    voice: null,
    userVoicesMap: {},
    loading: {
        isLoading: false,
        loadingType: null,
    },
    isSuccess: false,
};
const handlePending = (state, loadingType) => {
    state.loading = {
        loadingType,
        isLoading: true,
    };
};
const resetLoading = (state) => {
    state.loading = {
        loadingType: null,
        isLoading: false,
    };
};
export const getUserVoices = createAsyncThunk(UserVoiceActions.GET_USER_VOICES, async (_, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_VOICES}`;
        const response = await axiosClient.get(apiUrl);
        return response.data;
    }
    catch (error) {
        return thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const getUserVoiceById = createAsyncThunk(UserVoiceActions.GET_USER_VOICE_BY_ID, async (voiceId, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_VOICES}/${voiceId}`;
        const response = await axiosClient.get(apiUrl);
        return response.data;
    }
    catch (error) {
        return thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const cloneUserVoice = createAsyncThunk(UserVoiceActions.CLONE_USER_VOICE, async (payload, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_VOICES}`;
        const response = await axiosClient.post(apiUrl, {
            ...payload,
        });
        return response.data;
    }
    catch (error) {
        return thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const resumeUserVoice = createAsyncThunk(UserVoiceActions.RESUME_USER_VOICE, async (payload, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_VOICES}/${payload.voice_id}/resume`;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- voice_id is used in the URL path, exclude it from the body
        const { voice_id, sampleIds, sampleFiles, ...rest } = payload;
        const response = await axiosClient.post(apiUrl, {
            ...rest,
            sample_ids: sampleIds,
            sample_files: sampleFiles,
        });
        return response.data;
    }
    catch (error) {
        return thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const updateUserVoice = createAsyncThunk(UserVoiceActions.UPDATE_USER_VOICE, async (payload, thunkAPI) => {
    try {
        const { voice_id, ...body } = payload;
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_VOICES}/${voice_id}`;
        const response = await axiosClient.patch(apiUrl, body);
        return response.data;
    }
    catch (error) {
        return thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const deleteUserVoice = createAsyncThunk(UserVoiceActions.DELETE_USER_VOICE, async (voiceId, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_VOICES}/${voiceId}`;
        await axiosClient.delete(apiUrl);
        return voiceId;
    }
    catch (error) {
        return thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const approveUserVoice = createAsyncThunk(UserVoiceActions.APPROVE_USER_VOICE, async (payload, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_VOICES}/${payload.voice_id}/approve`;
        const response = await axiosClient.post(apiUrl, { preview_sample: payload.preview_sample });
        return response.data;
    }
    catch (error) {
        return thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const markVoiceAsProcessing = createAsyncThunk(UserVoiceActions.MARK_VOICE_PROCESSING, async (payload, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH_VOICES}/${payload.voice_id}/pending-processing`;
        const response = await axiosClient.post(apiUrl, {});
        return response.data;
    }
    catch (error) {
        return thunkAPI.rejectWithValue(error?.response?.data);
    }
});
export const userVoicesSlice = createSlice({
    name: "userVoices",
    initialState,
    reducers: {
        clearUserVoices: (state) => {
            state.userVoices = [];
            state.userVoicesMap = {};
            state.voice = null;
        },
        setIsSuccess: (state, action) => {
            state.isSuccess = action.payload;
        },
    },
    extraReducers(builder) {
        builder.addCase(getUserVoices.pending, (state) => {
            handlePending(state, UserVoiceActions.GET_USER_VOICES);
        });
        builder.addCase(getUserVoices.fulfilled, (state, action) => {
            resetLoading(state);
            state.userVoices = action.payload;
            state.userVoicesMap = buildVoicesMap(action.payload);
        });
        builder.addCase(getUserVoices.rejected, (state) => {
            resetLoading(state);
        });
        builder.addCase(getUserVoiceById.pending, (state) => {
            handlePending(state, UserVoiceActions.GET_USER_VOICE_BY_ID);
        });
        builder
            .addCase(getUserVoiceById.fulfilled, (state, action) => {
            resetLoading(state);
            state.voice = action.payload;
        })
            .addCase(getUserVoiceById.rejected, (state) => {
            resetLoading(state);
        });
        builder.addCase(updateUserVoice.pending, (state) => {
            handlePending(state, UserVoiceActions.UPDATE_USER_VOICE);
        });
        builder.addCase(updateUserVoice.fulfilled, (state) => {
            resetLoading(state);
        });
        builder
            .addCase(updateUserVoice.rejected, (state) => {
            resetLoading(state);
        })
            .addCase(deleteUserVoice.pending, (state) => {
            handlePending(state, UserVoiceActions.DELETE_USER_VOICE);
        });
        builder.addCase(deleteUserVoice.fulfilled, (state, action) => {
            resetLoading(state);
            state.userVoices = state.userVoices.filter((voice) => voice.id !== action.payload);
            delete state.userVoicesMap[action.payload];
        });
        builder.addCase(deleteUserVoice.rejected, (state) => {
            resetLoading(state);
        });
        builder.addCase(cloneUserVoice.pending, (state) => {
            handlePending(state, UserVoiceActions.CLONE_USER_VOICE);
        });
        builder.addCase(cloneUserVoice.fulfilled, (state) => {
            resetLoading(state);
        });
        builder.addCase(cloneUserVoice.rejected, (state) => {
            resetLoading(state);
        });
        builder.addCase(resumeUserVoice.pending, (state) => {
            handlePending(state, UserVoiceActions.RESUME_USER_VOICE);
        });
        builder.addCase(resumeUserVoice.fulfilled, (state) => {
            resetLoading(state);
        });
        builder.addCase(resumeUserVoice.rejected, (state) => {
            resetLoading(state);
        });
        builder.addCase(approveUserVoice.pending, (state) => {
            handlePending(state, UserVoiceActions.APPROVE_USER_VOICE);
            state.isSuccess = false;
        });
        builder.addCase(approveUserVoice.fulfilled, (state) => {
            resetLoading(state);
            state.isSuccess = true;
        });
        builder.addCase(approveUserVoice.rejected, (state) => {
            resetLoading(state);
            state.isSuccess = false;
        });
        builder.addCase(markVoiceAsProcessing.pending, (state) => {
            handlePending(state, UserVoiceActions.MARK_VOICE_PROCESSING);
        });
        builder.addCase(markVoiceAsProcessing.fulfilled, (state) => {
            resetLoading(state);
        });
        builder.addCase(markVoiceAsProcessing.rejected, (state) => {
            resetLoading(state);
        });
    },
});
export const { clearUserVoices, setIsSuccess } = userVoicesSlice.actions;
export default userVoicesSlice.reducer;
